// src/utils/imageMap.ts

export type TileImageMap = Record<string, string[]>;
export type ImageModule = { default: string };

// normalize filenames to keys (lowercase, strip extension & hash suffix)
function normalizeKey(filename: string): string {
  const base = filename.toLowerCase().replace(/\.[^.]+$/, ""); // remove extension
  return base.replace(/-[A-Za-z0-9]+$/, ""); // strip hash suffix
}

// Auto-discover all images in tile-images directory using Vite's import.meta.glob
const imageModules = import.meta.glob("/public/tile-images/*", {
  eager: true,
  query: "?url",
  import: "default",
});

// Build a map of normalized names to their actual file paths
const discoveredImages: Record<string, string> = {};
Object.keys(imageModules).forEach((path) => {
  const filename = path.split("/").pop() || "";
  const normalizedName = normalizeKey(filename);
  discoveredImages[normalizedName] = `/tile-images/${filename}`;
});

// Dynamic function to get image by name from discovered images
export function getImageUrl(imageName: string): string | null {
  const normalizedName = normalizeKey(imageName);
  return discoveredImages[normalizedName] || null;
}

// Get all available image names (normalized keys)
export function getAvailableImageNames(): string[] {
  return Object.keys(discoveredImages).sort();
}

// For backward compatibility, create a simple map that generates URLs on demand
const tileImageMap: TileImageMap = new Proxy(
  {},
  {
    get(target, prop) {
      if (typeof prop === "string") {
        const imageUrl = getImageUrl(prop);
        return imageUrl ? [imageUrl] : [];
      }
      return target[prop as keyof typeof target];
    },
  },
);

export default tileImageMap;
